'use strict'

// PWA OOTB Hooks
import React, {useMemo, useState} from 'react'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from './use-multi-site'
import useSiteType from './use-site-type'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {
    setLocalStorageJSONItem,
    getLocalStorageJSONItem,
    clearLocalStorageJSONItem,
    getContactData
} from './../utils/utils'
import useShipToCountry from './use-ship-to-country'
import {useGTM} from '@app/hooks/use-gtm'

/**
 * Custom hook that provides functionality to interact with the Splio service.
 *
 * @returns {Object} An object containing the `sendBasketToSplio` function.
 *
 * @property {Function} sendBasketToSplio - Sends the current basket information to the Splio service.
 *
 * @function
 * @name sendBasketToSplio
 * @async
 * @returns {Promise<Object>} A promise that resolves to an object containing the success status and message.
 * @property {boolean} success - Indicates whether the operation was successful.
 * @property {string} message - A message providing additional information about the operation's result.
 *
 * @throws {Error} If there is an issue with the network request or any other error occurs during the process.
 */
export const useSplioService = () => {
    const {shipToCountry} = useShipToCountry()
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()
    const {site, locale} = useMultiSite()
    const {isEU, isOutlet} = useSiteType()
    const {data: customer} = useCurrentCustomer()
    let [currentCustomerEmail, setCurrentCustomerEmail] = useState(null)
    let [splioAccountData, setSplioAccountData] = useState(null)
    let accessToken = ''
    const {triggerNewsletterSubscriptionEvent} = useGTM()

    useMemo(async () => {
        try {
            accessToken = await getTokenWhenReady()
            let contactDataResult = await getContactData({
                contact_id: currentCustomerEmail,
                locale: locale.id,
                siteId: site.id,
                accessToken: accessToken,
                orgId: appConfig.commerceAPI.parameters.organizationId
            })

            if (contactDataResult.success) {
                setSplioAccountData(contactDataResult.data ?? null)
            } else {
                setSplioAccountData(null)
            }
        } catch (error) {
            return error
        }
    }, [currentCustomerEmail])

    return {
        /**
         * Asynchronously checks if a given email address is blacklisted (already subscribed) using the Splio service.
         *
         * @param {Object} params - Parameters for the blacklist check.
         * @param {string} params.email - The email address to check.
         * @param {string} params.locale - The locale to use for the request.
         * @param {string} params.siteId - The site identifier.
         * @param {string} params.requestType - The type of request being made.
         * @returns {Promise<Object>} Resolves to an object containing:
         *   - {boolean} success - Indicates if the email is blacklisted (already subscribed).
         *   - {string} message - A message describing the result.
         *
         * @throws {Error} If the fetch request fails, returns an object with success set to false and a message.
         */
        async isBlackListedEmail(params) {
            let blackListResult = {
                success: false,
                message: ''
            }

            try {
                accessToken = await getTokenWhenReady()

                let body = {
                    contact_id: params.email,
                    locale: params.locale,
                    siteId: params.siteId,
                    accessToken: accessToken,
                    orgId: appConfig.commerceAPI.parameters.organizationId,
                    requestType: params.requestType
                }

                const response = await fetch('/api/splio/is-blacklisted-email', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    ...(body && {
                        body: JSON.stringify(body)
                    })
                })

                const result = await response.json()

                if (result.success) {
                    blackListResult.success = true
                    blackListResult.message = 'This email is already subscribed.'
                } else {
                    blackListResult.success = false
                    blackListResult.message = ''
                }

                return blackListResult
            } catch (error) {
                blackListResult.success = false
                blackListResult.message = 'This email is already subscribed.'

                return blackListResult
            }
        },

        /**
         * Checks if a given email address is already subscribed to the Splio newsletter for a specific site and locale.
         *
         * This function retrieves contact data from Splio (either from a cached object or via an API call) and determines
         * if the user is registered to the appropriate Splio list based on the site identifier. It handles errors such as
         * missing accounts or lists, and returns a result object indicating the subscription status.
         *
         * @async
         * @param {Object} params - Parameters for the subscription check.
         * @param {string} params.email - The email address to check for subscription.
         * @param {string} params.locale - The locale associated with the request.
         * @param {string} params.siteId - The site identifier used to determine the Splio list.
         * @param {string} params.requestType - The type of request being made (e.g., 'get', 'update').
         * @returns {Promise<Object>} An object containing:
         *   - {boolean} success - Whether the email is already subscribed.
         *   - {string} message - A descriptive message about the result.
         */
        async isAlreadySubscribed(params) {
            const newsletterResult = {
                success: false,
                message: ''
            }

            // set default result object
            let result = {
                success: true,
                data: ''
            }

            try {
                accessToken = await getTokenWhenReady()
                let contactDataResult

                if (!splioAccountData) {
                    result = await getContactData({
                        contact_id: params.email,
                        locale: params.locale,
                        siteId: params.siteId,
                        accessToken: accessToken,
                        orgId: appConfig.commerceAPI.parameters.organizationId,
                        requestType: params.requestType
                    })
                } else {
                    contactDataResult = splioAccountData
                }

                if (!result.success) {
                    throw new Error(result.message)
                }

                if (result.success && result.data) {
                    // update contactDataResult with the data received from the service call, if it is the case
                    contactDataResult = result.data
                }

                if (!contactDataResult) {
                    throw new Error('Account does not exist in Splio')
                }

                if (!contactDataResult.lists || contactDataResult.lists.length == 0) {
                    throw new Error('Account does not exist in Splio')
                }

                let splioListId = params.siteId.indexOf('OUTLET') > -1 ? 1 : 0
                let isRegistered = contactDataResult.lists.some(function (list) {
                    return list.id == splioListId
                })

                if (!isRegistered) {
                    throw new Error('Account does not exist in Splio')
                }

                newsletterResult.success = true
                newsletterResult.message = 'This email is already subscribed.'

                return newsletterResult
            } catch (error) {
                newsletterResult.success = false
                newsletterResult.message = error.message

                return newsletterResult
            }
        },

        /**
         * Forwards customer account data to the Splio service for synchronization.
         *
         * Sends a POST request with customer and contextual information to the Splio integration endpoint.
         * Handles token acquisition, request construction, and response processing, updating local state as needed.
         *
         * @async
         * @param {Object} data - The customer account data to forward.
         * @param {string} data.email - The customer's email address (required).
         * @param {string} [data.firstName] - The customer's first name.
         * @param {string} [data.lastName] - The customer's last name.
         * @param {string} [data.phoneHome] - The customer's phone number.
         * @param {string} [data.source] - The source of the account creation.
         * @param {string} [data.landingpage] - The landing page identifier.
         * @param {boolean|string} [data.marketingConsent] - The customer's marketing consent status.
         * @param {string} [data.requestType] - The type of request being made.
         * @returns {Promise<Object>} An object containing the result of the operation:
         *   - {boolean} success - Indicates if the forwarding was successful.
         *   - {string} message - A message describing the result or error.
         *   - {Object|null} data - The data returned from Splio, if any.
         */
        async forwardAccountToSplio(data) {
            let accountResult = {
                success: false,
                message: '',
                data: null
            }

            const customerEmail = data.email

            if (!customerEmail) {
                return accountResult
            }

            accessToken = await getTokenWhenReady()

            try {
                setCurrentCustomerEmail(customerEmail)

                const body = {
                    contact_id: customerEmail,
                    content: {
                        firstname: data.firstName,
                        lastname: data.lastName,
                        cellphone: data.phoneHome
                    },
                    isOutlet: isOutlet,
                    shipToCountry: shipToCountry.id,
                    locale: locale.id,
                    siteId: site.id,
                    source: data.source || '',
                    landingpage: data.landingpage || '',
                    title: {
                        value: ''
                    },
                    accessToken: accessToken,
                    orgId: appConfig.commerceAPI.parameters.organizationId,
                    splioAccountData: splioAccountData,
                    marketingConsent: data.marketingConsent ? data.marketingConsent : '',
                    requestType: data.requestType
                }

                const response = await fetch('/api/splio/forward-account', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    ...(body && {
                        body: JSON.stringify(body)
                    })
                })

                const result = await response.json()

                if (!result.success) {
                    throw new Error(result.message)
                }

                setSplioAccountData(result.data)

                accountResult.success = result.success
                accountResult.message = result.message
                /* GTM Integration */
                triggerNewsletterSubscriptionEvent(data.newsletterSection)
            } catch (error) {
                accountResult.success = false
                accountResult.message = error.message
            }
        },

        /**
         * Unsubscribes a user account from Splio services by making a POST request to the backend API.
         *
         * @async
         * @param {Object} params - Parameters for the unsubscribe request.
         * @param {string} params.id - The unique identifier of the account to unsubscribe.
         * @param {string} [params.requestType] - Optional type of unsubscribe request.
         * @returns {Promise<Object>} An object containing the result of the unsubscribe operation:
         *   - {boolean} success - Indicates if the operation was successful.
         *   - {string} message - A message describing the result or error.
         *   - {any} data - Additional data (currently always null).
         */
        async unsubscribeAccount(params) {
            let accountResult = {
                success: false,
                message: '',
                data: null
            }

            if (!params || !params.id) {
                return accountResult
            }

            accessToken = await getTokenWhenReady()

            try {
                const body = {
                    id: params.id,
                    locale: locale.id,
                    siteId: site.id,
                    accessToken: accessToken,
                    orgId: appConfig.commerceAPI.parameters.organizationId,
                    requestType: params.requestType
                }

                const response = await fetch('/api/splio/unsubscribe-account', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    ...(body && {
                        body: JSON.stringify(body)
                    })
                })

                if (!response.ok) {
                    return accountResult
                }

                const result = await response.json()

                accountResult.success = result.success
                accountResult.message = result.message

                return accountResult
            } catch (e) {
                accountResult.success = false
                accountResult.message = e.message
                return accountResult
            }
        },

        /**
         * Sends the current basket data to the Splio service for processing, including customer, store, and product details.
         *
         * @async
         * @param {Object} data - The data required to send the basket to Splio.
         * @param {boolean} data.isCompleted - Indicates if the basket is completed (order placed).
         * @param {Object} data.basket - The basket object containing customer and product information.
         * @param {string} data.requestType - The type of request being made to Splio.
         * @returns {Promise<Object>} An object containing the result of the operation with properties:
         *   - {boolean} success: Whether the operation was successful.
         *   - {string} message: A message describing the result or error.
         */
        async sendBasketToSplio(data) {
            const {isCompleted, basket, requestType} = data
            let basketResult = {
                success: false,
                message: ''
            }

            accessToken = await getTokenWhenReady()

            try {
                const customerEmail = basket?.customerInfo?.email || customer?.email
                const totalPrice = basket?.orderTotal || basket?.productTotal
                if (!basket || !customerEmail || !totalPrice) {
                    return basketResult
                }

                setCurrentCustomerEmail(customerEmail)

                let storeExternalId = ''

                if (isEU) {
                    storeExternalId = '802'
                } else {
                    storeExternalId = '807'
                }

                let body = {
                    external_id: basket?.basketId,
                    contact_id: customerEmail,
                    store_external_id: storeExternalId,
                    completed: isCompleted || false,
                    products: [],
                    shipToCountry: shipToCountry.id,
                    total_price: totalPrice,
                    locale: locale.id,
                    siteId: site.id,
                    accessToken: accessToken,
                    orgId: appConfig.commerceAPI.parameters.organizationId,
                    splioAccountData: splioAccountData,
                    requestType: requestType
                }

                for (let i = 0; i < basket?.productItems?.length; i++) {
                    const product = basket?.productItems[i]
                    body.products.push({
                        product_id: product.productId,
                        quantity: product.quantity,
                        currency: basket?.currency,
                        total_line_amount: product.price,
                        content: {
                            external_id: basket?.basketId
                        }
                    })
                }

                const response = await fetch('/api/splio/send-basket', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    ...(body && {
                        body: JSON.stringify(body)
                    })
                })

                const result = await response.json()

                if (result.success) {
                    setLocalStorageJSONItem('currentBasketId', {basketId: basket?.basketId})
                }

                basketResult.success = result.success
                basketResult.message = result.message
            } catch (error) {
                basketResult.success = false
                basketResult.message = error.message
            }

            return basketResult
        },

        /**
         * Removes the current basket from Splio by sending a delete request to the Splio API.
         *
         * Retrieves the current basket ID from local storage, constructs the request payload with necessary
         * authentication and context information, and sends a POST request to the '/api/splio/delete-order' endpoint.
         * If the operation is successful, the basket ID is cleared from local storage.
         *
         * @async
         * @param {Object} data - The data object containing request parameters.
         * @param {string} data.requestType - The type of request to be sent to the Splio API.
         * @returns {Promise<Object>} An object containing the result of the operation:
         *   - {boolean} success - Indicates if the basket was successfully removed.
         *   - {string} message - A message describing the result or any error encountered.
         */
        async removeBasketFromSplio(data) {
            const {requestType} = data
            let basketResult = {
                success: false,
                message: ''
            }

            try {
                const basketIdObj = getLocalStorageJSONItem('currentBasketId')

                if (!basketIdObj) {
                    return basketResult
                }

                const accessToken = await getTokenWhenReady()
                const body = {
                    id: basketIdObj?.basketId,
                    siteId: site.id,
                    locale: locale.id,
                    accessToken: accessToken,
                    orgId: appConfig.commerceAPI.parameters.organizationId,
                    requestType: requestType
                }

                const response = await fetch('/api/splio/delete-order', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    ...(body && {
                        body: JSON.stringify(body)
                    })
                })

                const result = await response.json()

                if (result.success) {
                    clearLocalStorageJSONItem('currentBasketId')
                }

                basketResult.success = result.success
                basketResult.message = result.message
            } catch (error) {
                basketResult.success = false
                basketResult.message = error.message
            }

            return basketResult
        }
    }
}
